/**
 * Deal-category AI proposal schemas
 * ─────────────────────────────────
 * Structured outputs the deal-category authoring proposers return (and validate
 * via `generateValidated`) before the admin confirms and the apply step persists
 * them. Each proposer blends the selected sources (base clone / past deals /
 * AI research / paste) by AI judgement and grounds in the workspace's own records
 * plus, for AI-research sources, public web `sources`.
 *
 * Kept here in `@protoss/shared` so the runtime tools, the API facade, and any
 * tests share one shape. Confidence mirrors the estimate-inference band so the
 * confidence-gating policy treats every proposal the same way.
 */
import { z } from 'zod';
/** Confidence band a proposer reports - same enum as role-allocation inference. */
export const proposalConfidenceSchema = z.enum(['high', 'medium', 'low']);
/** A web source cited by a grounded ("AI Research") proposer. */
export const groundingSourceSchema = z.object({
    uri: z.string().min(1),
    title: z.string().optional(),
});
// ── Category name suggestions ───────────────────────────────────────────────────
/** One suggested category name (already in a clean human label form). */
export const categoryNameSuggestionSchema = z.object({
    name: z.string().min(1),
    rationale: z.string().optional(),
});
/** Up to 3 typical category-name suggestions for a product, grounded online. */
export const categoryNameSuggestionsSchema = z.object({
    suggestions: z.array(categoryNameSuggestionSchema).max(3).default([]),
    sources: z.array(groundingSourceSchema).default([]),
});
// ── Roles / functions / rates proposal ──────────────────────────────────────────
/** A single rate-card role with its function description and (benchmark) rate. */
export const categoryRoleProposalEntrySchema = z.object({
    /** Stable role_id when matching/adjusting an existing rate-card role; omit for a new role. */
    roleId: z.string().optional(),
    roleName: z.string().min(1),
    description: z.string().optional(),
    /** Hourly bill rate. For AI-research sources this is a public benchmark, not the customer's. */
    rate: z.number().nonnegative().optional(),
});
/** Proposed rate-card roles/functions/rates for a category. */
export const categoryRolesProposalSchema = z.object({
    roles: z.array(categoryRoleProposalEntrySchema).min(1),
    rationale: z.string().default(''),
    confidence: proposalConfidenceSchema,
    /** True when any `rate` is a public benchmark (AI-research source) rather than the customer's. */
    ratesAreBenchmarks: z.boolean().default(false),
    sources: z.array(groundingSourceSchema).default([]),
});
//# sourceMappingURL=category-proposals.js.map